import React, { ReactNode, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { Checkbox, Input, Switch, Text } from '@fluentui/react-components';
import {
  Add24Filled,
  Delete24Filled,
  Save24Filled,
  WrenchSettings24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button';
import { DropDownHours } from '../../DropdownHours';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import {
  ChatLockMode,
  ChatLockResponse,
  ChatUserType,
  DayOfWeek,
  GuardianChatThreadResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { ChatSettingsParticipantParent } from './ChatSettingsParticipantParent';
import { ChatSettingsParticipantTeacher } from './ChatSettingsParticipantTeacher';
import { lockTimeToDate, lockTimeToMinutes } from './ChatSettings.utils';
import { useChatContextOptional } from '../ChatContext';
import './ChatSettings.scss';

interface HeadlineProps {
  icon?: ReactNode;
  content: string;
  action?: ReactNode;
}

const Headline: React.FC<HeadlineProps> = ({ icon, content, action }) => {
  return (
    <div className="Headline">
      {icon}
      <Text size={400} weight="bold" className="text">
        {content}
      </Text>
      {action}
    </div>
  );
};

interface HeadlineBadgeProps {
  count: number;
}

const HeadlineBadge: React.FC<HeadlineBadgeProps> = ({ count }) => {
  return (
    <div className="HeadlineBadge">
      <span>{count}</span>
    </div>
  );
};

interface SettingsToggleProps {
  headline: string;
  description: string;
  checked?: boolean;
  onCheckChange: (checked: boolean) => void;
}

const SettingsToggle: React.FC<SettingsToggleProps> = ({
  headline,
  description,
  checked,
  onCheckChange,
}) => {
  return (
    <div className="SwitchToggle">
      <div className="switch">
        <Switch
          label={<Text weight="bold">{headline}</Text>}
          checked={checked}
          onChange={(_event, data) => onCheckChange(data.checked)}
        />
      </div>
      <div className="description">
        <Text>{description}</Text>
      </div>
    </div>
  );
};

export interface ChatSettingsProps {
  thread?: GuardianChatThreadResponse;
  onAddParticipantClick?: () => void;
  onRemoveParticipantClick?: (userId: string) => void;
}

export const ChatSettings: React.FC<ChatSettingsProps> = (props) => {
  const { t } = useTranslation();
  const { getConfirmation } = useConfirmationDialog();
  const context = useChatContextOptional();
  const thread = context?.selectedThread ?? props.thread;
  const selfAcsUserId = context?.selfAcsUserId;

  const [formValues, setFormValues] = useState<ChatLockResponse>(
    () =>
      thread?.lockConfiguration ?? {
        mode: ChatLockMode.none,
        isCurrentlyLocked: false,
      },
  );

  const isLockTimeRangeError = useMemo<boolean>(() => {
    if (formValues.mode !== ChatLockMode.timeBased) {
      return false;
    }

    const openMinutes = lockTimeToMinutes(formValues.openTime);
    const closeMinutes = lockTimeToMinutes(formValues.closeTime);

    if (openMinutes === null || closeMinutes === null) {
      return false;
    }

    return openMinutes >= closeMinutes;
  }, [formValues.mode, formValues.openTime, formValues.closeTime, t]);

  const onSettingsChange = context?.updateSettings;

  const setAddParticipantsDialogVisible = context?.setAddParticipantsDialogVisible;

  const onAddTeacherClick = setAddParticipantsDialogVisible
    ? () => setAddParticipantsDialogVisible(true, true)
    : props.onAddParticipantClick;

  const onAddGuardianClick = setAddParticipantsDialogVisible
    ? () => setAddParticipantsDialogVisible(true, false)
    : props.onAddParticipantClick;

  const onRemoveParticipantClick =
    context?.removeParticipantFromActiveThread ?? props.onRemoveParticipantClick;

  const onDeleteChat = context?.deleteChat;

  const handleDeleteChat = async () => {
    if (!onDeleteChat) {
      return;
    }

    const confirmed = await getConfirmation({
      content: t('pc:chat:delete_chat_confirmation', {
        defaultValue: 'Möchten Sie diesen Chat wirklich löschen?',
      }),
      headerText: t('are_you_sure', { defaultValue: 'Sind Sie sicher?' }),
      confirmButtonText: t('delete', { defaultValue: 'Löschen' }),
      cancelButtonText: t('cancel', { defaultValue: 'Abbrechen' }),
      showCloseButton: false,
    });
    if (confirmed) {
      onDeleteChat();
    }
  };

  if (!thread) {
    return <></>;
  }

  const openTimeDate = lockTimeToDate(formValues.openTime, 8);
  const closeTimeDate = lockTimeToDate(formValues.closeTime, 16);
  const teachers = thread.participants.filter((p) => p.userType === ChatUserType.teacher);
  const parents = thread.participants.filter((p) => p.userType === ChatUserType.parent);

  // Only the chat creator may remove other participants.
  const currentUserParticipant = selfAcsUserId
    ? thread.participants.find((p) => p.acsUserId === selfAcsUserId)
    : undefined;
  const isCurrentUserCreator = currentUserParticipant?.userId === thread.createdById;

  return (
    <div className="ChatSettings">
      <Input
        placeholder={t('pc:chat:search_participants', { defaultValue: 'Teilnehmer suchen...' })}
        aria-label={t('pc:chat:search_participants', { defaultValue: 'Teilnehmer suchen...' })}
        type="search"
      />
      <div className="participantsTeachers">
        <Headline
          icon={<HeadlineBadge count={teachers.length} />}
          content={t('pc:chat:teachers', { defaultValue: 'Lehrkräfte' })}
          action={
            onAddTeacherClick ? (
              <Button
                icon={<Add24Filled />}
                appearance="transparent"
                className="primaryBtn"
                aria-label={t('pc:chat:settings:add_teacher_aria', {
                  defaultValue: 'Lehrkraft hinzufügen',
                })}
                onClick={onAddTeacherClick}
              />
            ) : undefined
          }
        />
        {sortBy(teachers, ['displayName']).map((teacher) => {
          const isSelf = teacher.acsUserId === selfAcsUserId;
          const isCreator = teacher.userId === thread.createdById;

          return (
            <ChatSettingsParticipantTeacher
              key={teacher.userId}
              teacher={teacher}
              onDeleteClick={
                isCurrentUserCreator && !isSelf && !isCreator ? onRemoveParticipantClick : undefined
              }
              isOwner={isCreator}
            />
          );
        })}
      </div>
      <div className="participantsStudents">
        <Headline
          icon={<HeadlineBadge count={parents.length} />}
          content={t('pc:chat:guardians', { defaultValue: 'Erziehungsberechtigte' })}
          action={
            onAddGuardianClick ? (
              <Button
                icon={<Add24Filled />}
                appearance="transparent"
                className="primaryBtn"
                aria-label={t('pc:chat:settings:add_guardian_aria', {
                  defaultValue: 'Erziehungsberechtigte hinzufügen',
                })}
                onClick={onAddGuardianClick}
              />
            ) : undefined
          }
        />
        {parents.map((parent) => (
          <ChatSettingsParticipantParent
            key={parent.userId}
            parent={parent}
            onDeleteClick={isCurrentUserCreator ? onRemoveParticipantClick : undefined}
          />
        ))}
      </div>
      {onSettingsChange && (
        <div className="generalSettings">
          <Headline
            icon={<WrenchSettings24Regular color="#9E9E9E" />}
            content={t('pc:chat:settings:headline', { defaultValue: 'Einstellungen' })}
          />
          <SettingsToggle
            headline={t('pc:chat:locked_chat', { defaultValue: 'Gesperrter Chat' })}
            description={t('pc:chat:locked_chat_description', {
              defaultValue:
                'In gesperrten Chats dürfen nur Lehrkräfte neue Nachrichten schreiben. Erziehungsberechtigte können nur lesen.',
            })}
            checked={formValues?.mode === ChatLockMode.locked}
            onCheckChange={(checked) =>
              setFormValues((prev) => ({
                ...prev,
                mode: checked ? ChatLockMode.locked : ChatLockMode.none,
                allowedDaysOfWeek: null,
                closeTime: null,
                openTime: null,
                timeZone: null,
              }))
            }
          />
          <SettingsToggle
            headline={t('pc:chat:write_times', { defaultValue: 'Schreibzeiten' })}
            description={t('pc:chat:write_times_description', {
              defaultValue:
                'Im Chat kann von Erziehungsberechtigten nur zwischen den folgenden Zeiten geschrieben werden:',
            })}
            checked={formValues?.mode === ChatLockMode.timeBased}
            onCheckChange={(checked) => {
              if (checked) {
                setFormValues((prev) => ({
                  ...prev,
                  mode: ChatLockMode.timeBased,
                  openTime: '08:00',
                  closeTime: '16:00',
                  allowedDaysOfWeek: checked
                    ? prev.allowedDaysOfWeek ?? [
                        DayOfWeek.monday,
                        DayOfWeek.tuesday,
                        DayOfWeek.wednesday,
                        DayOfWeek.thursday,
                        DayOfWeek.friday,
                      ]
                    : prev.allowedDaysOfWeek,
                }));
              } else {
                setFormValues((prev) => ({
                  ...prev,
                  mode: ChatLockMode.none,
                  allowedDaysOfWeek: null,
                  closeTime: null,
                  openTime: null,
                  timeZone: null,
                }));
              }
            }}
          />
          {formValues?.mode === ChatLockMode.timeBased && (
            <div className="flex flex-col gap-2 overflow-hidden">
              <div className="lockTimeInput flex flex-col gap-2">
                <div className="flex flex-row gap-1 items-center">
                  <Text>{t('pc:chat:between', { defaultValue: 'Zwischen' })}</Text>
                  <DropDownHours
                    className="lockTimeDropdown"
                    event={openTimeDate}
                    handleTimeChange={(selectedTime) =>
                      setFormValues((prev) => ({
                        ...prev,
                        openTime: `${selectedTime.hour}:${selectedTime.minute}`,
                      }))
                    }
                    aria-label={t('pc:chat:settings_locktime_from_aria_label', {
                      defaultValue:
                        'Öffnet einen Flyout, um festzulegen, ab welcher Uhrzeit Erziehungsberechtigte im Chat schreiben dürfen',
                    })}
                  />
                </div>
                <div className="flex flex-row gap-1 items-center">
                  <Text>{t('pc:chat:and', { defaultValue: 'Und' })}</Text>
                  <DropDownHours
                    className="lockTimeDropdown"
                    event={closeTimeDate}
                    handleTimeChange={(selectedTime) =>
                      setFormValues((prev) => ({
                        ...prev,
                        closeTime: `${selectedTime.hour}:${selectedTime.minute}`,
                      }))
                    }
                    aria-label={t('pc:chat:settings_locktime_to_aria_label', {
                      defaultValue:
                        'Öffnet einen Flyout, um festzulegen, bis zu welcher Uhrzeit Erziehungsberechtigte im Chat schreiben dürfen',
                    })}
                  />
                </div>
                {isLockTimeRangeError && (
                  <Text role="alert" className="lockTimeRangeError">
                    {t('pc:chat:write_times_invalid_order', {
                      defaultValue: 'Die Startzeit muss vor der Endzeit liegen.',
                    })}
                  </Text>
                )}
              </div>
              <div className="lockTimeWeekdays flex flex-col gap-1">
                {[
                  {
                    day: DayOfWeek.monday,
                    label: t('pc:chat:weekday_monday', { defaultValue: 'Montag' }),
                  },
                  {
                    day: DayOfWeek.tuesday,
                    label: t('pc:chat:weekday_tuesday', { defaultValue: 'Dienstag' }),
                  },
                  {
                    day: DayOfWeek.wednesday,
                    label: t('pc:chat:weekday_wednesday', { defaultValue: 'Mittwoch' }),
                  },
                  {
                    day: DayOfWeek.thursday,
                    label: t('pc:chat:weekday_thursday', { defaultValue: 'Donnerstag' }),
                  },
                  {
                    day: DayOfWeek.friday,
                    label: t('pc:chat:weekday_friday', { defaultValue: 'Freitag' }),
                  },
                  {
                    day: DayOfWeek.saturday,
                    label: t('pc:chat:weekday_saturday', { defaultValue: 'Samstag' }),
                  },
                  {
                    day: DayOfWeek.sunday,
                    label: t('pc:chat:weekday_sunday', { defaultValue: 'Sonntag' }),
                  },
                ].map(({ day, label }) => (
                  <Checkbox
                    key={day}
                    label={label}
                    checked={formValues.allowedDaysOfWeek?.includes(day) ?? false}
                    onChange={(_event, data) =>
                      setFormValues((prev) => {
                        const current = prev.allowedDaysOfWeek ?? [];
                        return {
                          ...prev,
                          allowedDaysOfWeek: data.checked
                            ? [...current, day]
                            : current.filter((d) => d !== day),
                        };
                      })
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {(onSettingsChange || onDeleteChat) && (
        <div className="flex flex-col gap-1">
          {onSettingsChange && (
            <Button
              appearance="primary"
              onClick={() => onSettingsChange(formValues)}
              disabled={isLockTimeRangeError}
              icon={<Save24Filled />}
              // The button enforces a max width of 280px with "!important". Only way
              // to make this button use the full settings sidebar width is to use inline
              // styles here, to make sure the unset max-width is set last
              style={{ maxWidth: 'unset !important' }}
            >
              {t('pc:chat:save_settings', { defaultValue: 'Änderungen speichern' })}
            </Button>
          )}
          {onDeleteChat && (
            <Button
              appearance="secondary"
              onClick={handleDeleteChat}
              icon={<Delete24Filled />}
              style={{ maxWidth: 'unset !important' }}
            >
              {t('pc:chat:close_chat', { defaultValue: 'Chat schließen' })}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
